// Bottom-left collapsible legend: the map's colour key and nothing more (0051
// slice 3). The column's state words, each with the colour and the mark the
// canvas draws for it (nodeColorFor, nodeMarkFor), then the two kinds of link
// the map draws, each with the colour and the line the canvas draws it with
// (edgeColorAndDash). Swatches read from the active palette so the legend
// stays in sync with what the canvas actually draws under either theme.
import { useThemeStore } from "../../auth/useTheme";
import { MAP_EDGE_WORDS, STATE_WORD, type SpecRowState } from "../../lib/specLines";
import { edgeColorAndDash, nodeColorFor, nodeMarkFor, pickPalette } from "./GraphCanvas";

// The keyed states in order, each with the shape the canvas draws it in: a
// decision's square, a question's ring, or none for a saved item (it can be a
// decision or a question, so its mark alone keys it). An answered question
// reads the same word in the same colour as a saved item, so it is not listed
// twice.
const KEYED: readonly { state: SpecRowState; shape: "square" | "ring" | null }[] = [
  { state: "confirmed", shape: "square" },
  { state: "proposed", shape: "square" },
  { state: "blocked", shape: "square" },
  { state: "open", shape: "ring" },
  { state: "saved", shape: null },
];

// The two kinds of link the map draws (lib/specLines' mapDataOf), in order.
const LINKS = ["cites", "source"] as const;

export function Legend() {
  const effective = useThemeStore((s) => s.effective);
  const palette = pickPalette(effective);
  return (
    <div className="absolute bottom-4 left-4 bg-ink-900/80 backdrop-blur-sm border border-ink-800 rounded-md text-[11px] font-mono leading-tight max-w-xs">
      <details className="group">
        <summary className="px-3 py-2 cursor-pointer text-ink-300 hover:text-ink-100 list-none flex items-center justify-between gap-3">
          <span className="uppercase tracking-widest text-ink-500">Legend</span>
          <span className="text-ink-500 group-open:rotate-180 transition-transform">▾</span>
        </summary>
        <div className="px-3 pb-3 space-y-3 border-t border-ink-800 pt-2">
          <Group>
            {KEYED.map(({ state, shape }) => {
              const mark = nodeMarkFor(state);
              return (
                <Item
                  key={state}
                  color={nodeColorFor(state, palette)}
                  label={STATE_WORD[state]}
                  shape={shape}
                  dashed={mark.dashed}
                  glyph={mark.glyph}
                />
              );
            })}
          </Group>
          <Group>
            {LINKS.map((rel) => {
              const { color, dashed } = edgeColorAndDash(rel, palette);
              return <EdgeItem key={rel} color={color} label={MAP_EDGE_WORDS[rel]} dashed={dashed} />;
            })}
          </Group>
        </div>
      </details>
    </div>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

function Item({
  color,
  label,
  shape,
  dashed,
  glyph,
}: {
  color: string;
  label: string;
  shape: "square" | "ring" | null;
  dashed: boolean;
  glyph: string;
}) {
  return (
    <li className="flex items-center gap-2 text-ink-300">
      <span className="inline-flex w-5 shrink-0 items-center gap-1">
        {shape !== null && (
          <span
            data-testid="legend-swatch"
            className={`inline-block h-2.5 w-2.5 ${shape === "square" ? "rounded-sm" : "rounded-full"} shrink-0`}
            style={{
              backgroundColor: "transparent",
              border: `1.5px ${dashed ? "dashed" : "solid"} ${color}`,
            }}
          />
        )}
        {glyph !== "" && (
          <span aria-hidden="true" className="font-bold leading-none" style={{ color }}>
            {glyph}
          </span>
        )}
      </span>
      <span>{label}</span>
    </li>
  );
}

function EdgeItem({ color, label, dashed }: { color: string; label: string; dashed?: boolean }) {
  return (
    <li className="flex items-center gap-2 text-ink-300">
      <span
        data-testid="legend-line"
        className="inline-block h-px w-5 shrink-0"
        style={{
          background: dashed
            ? `repeating-linear-gradient(90deg, ${color} 0, ${color} 3px, transparent 3px, transparent 6px)`
            : color,
        }}
      />
      <span>{label}</span>
    </li>
  );
}
